import React, { useState } from 'react';
import BRAND from '../brandConfig';
import { useApp } from '../context/AppContext';
import { useI18n } from '../i18n';
import { fmtMoney } from '../lib/format';
import { PAYMENT_MODES, CREDENTIAL_TYPES, LEDGER_STATUSES, credentialLabel, balanceOf } from '../lib/ledger';
import MessageThread from '../components/MessageThread';
import ItemEditor from '../components/ItemEditor';
import DocEditor from '../components/DocEditor';
import ClientEditor from '../components/ClientEditor';
import MediaStrip from '../components/MediaStrip';
import RequestItemEditor from '../components/RequestItemEditor';
import { SERVICE_TYPES } from './RequestForm';
import { TAXONOMY } from './VendorDashboard';

// Lead sources the admin can pick when logging a lead manually.
const LEAD_SOURCES = ['Facebook', 'Nextdoor', 'Instagram', 'TikTok', 'Referral', 'Phone/Text', 'Google', 'Yelp', 'Website', 'Other'];

// All bottom-sheet modals (estimate, assign vendor, completion, invoice) plus
// their click-away overlays. Which one shows is driven by the `modal` state.
export default function Modals() {
  const {
    modal, setModal, activeRequest,
    estimateForm, setEstimateForm, handleSubmitEstimate,
    estimateForClient, setEstimateForClient, askDeleteEstimate,
    clientInvoiceForm, setClientInvoiceForm, handleSaveClientInvoice, isSavingInvoice,
    noteEdit, setNoteEdit, handleSaveNote, isSavingNote,
    assignVendorId, setAssignVendorId, vendors, handleAssignVendor,
    completionFiles, setCompletionFiles, completionNote, setCompletionNote, handleSubmitCompletion,
    invoiceForm, setInvoiceForm, invoiceTotal, handleSubmitInvoice,
    addLeadForm, setAddLeadForm, handleAddLead, editingLeadItem, setEditingLeadItem, uploadPhotos,
    addClientForm, setAddClientForm, handleAddClient, isSavingClient, lastClientLink, setLastClientLink,
    openClientForm,
    appointmentInput, setAppointmentInput, handleSetAppointment,
    passwordForm, setPasswordForm, handleChangePassword,
    accountForm, setAccountForm, handleCreateAccount,
    accountMemberForm, setAccountMemberForm, handleSaveAccountMember,
    adminPropertyForm, setAdminPropertyForm, handleCreateAdminProperty,
    propertyJobForm, setPropertyJobForm, handleCreatePropertyJob,
    lastInviteLink, setLastInviteLink, showToast,
    emailForm, setEmailForm, handleChangeEmail,
    confirmDelete, setConfirmDelete, handleConfirmDelete,
    currentUser, requests, activeEntry, isSavingLedger,
    ledgerForm, setLedgerForm, ledgerDocFiles, setLedgerDocFiles, handleSaveLedgerEntry,
    paymentForm, setPaymentForm, paymentBlock, handleRecordPayment,
    progressForm, setProgressForm, progressFiles, setProgressFiles, handleAddProgress,
    credentialForm, setCredentialForm, credentialFile, setCredentialFile, handleSaveCredential,
    clients, projects, activeProject, isSavingProject,
    projectForm, setProjectForm, handleSaveProject,
    lineForm, setLineForm, handleSaveProjectLine,
    clientPayForm, setClientPayForm, handleSaveClientPayment,
    expenseForm, setExpenseForm, receiptFile, setReceiptFile, handleSaveExpense,
    estimateForm2, setEstimateForm2, estimateItemsTotal, handleSubmitTechEstimate,
    vendorEditForm, setVendorEditForm, handleSaveVendorEdit, isSavingVendor,
    lastVendorLink, setLastVendorLink,
  } = useApp();
  const { t, optLabel } = useI18n(); // tech-facing modals translate; admin modals stay English

  const setLead = (patch) => setAddLeadForm(p => ({ ...p, ...patch }));
  const setLedger = (patch) => setLedgerForm(p => ({ ...p, ...patch }));
  const setPay = (patch) => setPaymentForm(p => ({ ...p, ...patch }));
  const setCred = (patch) => setCredentialForm(p => ({ ...p, ...patch }));
  const isAdmin = currentUser?.role === 'admin';
  const setProj = (patch) => setProjectForm(p => ({ ...p, ...patch }));
  const setLine = (patch) => setLineForm(p => ({ ...p, ...patch }));
  const setCPay = (patch) => setClientPayForm(p => ({ ...p, ...patch }));
  const setExp = (patch) => setExpenseForm(p => ({ ...p, ...patch }));
  const setEst = (patch) => setEstimateForm2(p => ({ ...p, ...patch }));

  // The jobs on the request being priced, as Julian reads them on the estimate
  // sheet. A request from before line items existed has none, so its
  // description stands in as a single job — he can still read and fix it.
  const requestNotes = activeRequest
    ? (activeRequest.items?.length
        ? activeRequest.items
        : (activeRequest.description ? [{ title: activeRequest.serviceType, description: activeRequest.description, descriptionOriginal: activeRequest.descriptionOriginal, photos: activeRequest.photos }] : []))
    : [];
  // Whether this sheet is writing an estimate or correcting one that exists —
  // the same form either way, but it should not call itself "Submit" when the
  // customer already has the thing in their portal.
  const hasEstimate = activeRequest?.estimate?.amount != null;
  const setVendorEdit = (patch) => setVendorEditForm(p => ({ ...p, ...patch }));
  // Index of the line currently open in its own editor screen, or null.
  const [editingItem, setEditingItem] = useState(null);
  const sel = { padding: '11px 13px', border: '1.5px solid var(--border)', borderRadius: 'var(--radius-sm)', background: 'var(--white)', color: 'var(--navy)', fontFamily: 'inherit', width: '100%' };

  return (
    <>
      {/* ── Estimate ───────────────────────────────────────────────────────
          Joist's Edit Estimate, on a full screen — see components/DocEditor.js.
          What the customer asked for sits above the lines, because pricing a
          job called "DRYWALL" from its title alone means leaving the sheet to
          read the note and the photos, and a sheet you have to leave to use is
          a sheet that gets guessed at. */}
      {modal === 'estimate' && (
        <DocEditor
          title={estimateForClient ? 'New Estimate' : hasEstimate ? 'Edit Estimate' : 'New Estimate'}
          clientName={estimateForClient
            ? estimateForClient.client.name
            : (activeRequest?.fullName || activeRequest?.homeownerId?.name || 'Client')}
          clientSub={estimateForClient
            ? [estimateForClient.client.phone,
               [estimateForClient.client.address, estimateForClient.client.city].filter(Boolean).join(', ')]
              .filter(Boolean).join(' · ')
            : [activeRequest?.serviceType, activeRequest?.serviceAddress].filter(Boolean).join(' · ')}
          banner={activeRequest?.estimateStatus === 'approved' ? (
            /* Said plainly, because it is the one surprising thing about
               editing: a changed estimate is a new offer, and the customer has
               to agree to the new one. */
            <div className="alert alert-amber" style={{ margin: 16 }}>
              This estimate has been approved. Saving changes sends it back to the customer as a new estimate to approve.
            </div>
          ) : null}
          header={
            <>
              {requestNotes.length > 0 && (
                <>
                  <div className="de-sec">What they asked for</div>
                  <div className="de-fields">
                  {requestNotes.map((it, i) => {
                    const editing = noteEdit?.index === i;
                    return (
                      <div key={i} style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 12, marginBottom: 8, background: editing ? '#fffef9' : 'var(--bg)' }}>
                        {editing ? (
                          <>
                            <input
                              className="li-input" style={{ width: '100%', marginBottom: 8 }}
                              value={noteEdit.title}
                              placeholder="What needs fixing"
                              onChange={e => setNoteEdit(p => ({ ...p, title: e.target.value }))}
                            />
                            <textarea
                              value={noteEdit.description}
                              placeholder="The note for this job…"
                              style={{ width: '100%', minHeight: 96, marginBottom: 8 }}
                              onChange={e => setNoteEdit(p => ({ ...p, description: e.target.value }))}
                            />
                            <div style={{ display: 'flex', gap: 8 }}>
                              <button className="btn btn-primary btn-sm" disabled={isSavingNote} onClick={handleSaveNote}>{isSavingNote ? 'Saving…' : 'Save note'}</button>
                              <button className="btn btn-ghost btn-sm" onClick={() => setNoteEdit(null)}>Cancel</button>
                            </div>
                          </>
                        ) : (
                          <>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start' }}>
                              <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--navy)' }}>
                                {requestNotes.length > 1 && <span style={{ color: 'var(--muted)', fontWeight: 400 }}>{i + 1}. </span>}
                                {it.title || it.serviceType || 'This job'}
                              </div>
                              <button className="btn btn-ghost btn-sm" onClick={() => setNoteEdit({ index: i, title: it.title || '', description: it.description || '' })}>Edit</button>
                            </div>
                            {it.description && it.description !== it.title && (
                              <div style={{ fontSize: 13, color: 'var(--slate)', lineHeight: 1.45, marginTop: 4, whiteSpace: 'pre-wrap' }}>{it.description}</div>
                            )}
                            {/* Shown only once he has changed something, and never
                                thrown away: the estimate is approved against what
                                the customer asked for, not against our tidy-up. */}
                            {it.descriptionOriginal && it.descriptionOriginal !== it.description && (
                              <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 6, paddingTop: 6, borderTop: '1px dashed var(--border)', whiteSpace: 'pre-wrap' }}>
                                <strong>Their words:</strong> {it.descriptionOriginal}
                              </div>
                            )}
                            <MediaStrip urls={it.photos} label="Photos" />
                          </>
                        )}
                      </div>
                    );
                  })}
                  <div style={{ height: 14 }}></div>
                  </div>
                </>
              )}
            </>
          }
          lines={estimateForm.breakdown}
          onChangeLines={(breakdown) => setEstimateForm(p => ({ ...p, breakdown }))}
          notes={estimateForm.notes}
          onChangeNotes={(notes) => setEstimateForm(p => ({ ...p, notes }))}
          doneLabel={estimateForClient ? 'Create Job & Send Estimate' : hasEstimate ? 'Save & Send Updated Estimate' : 'Send Estimate to Homeowner'}
          onDone={handleSubmitEstimate}
          // Only on an estimate that has been written and sent — there is
          // nothing to delete on one being typed for the first time.
          onDelete={activeRequest?.estimate?.submittedAt && askDeleteEstimate
            ? () => { setModal(null); askDeleteEstimate(activeRequest); }
            : undefined}
          deleteLabel="Delete Estimate"
          backLabel="Estimate"
          onCancel={() => { setModal(null); setEstimateForClient(null); setNoteEdit(null); }}
        />
      )}

      {/* Assign Vendor Modal */}
      <div className={`modal ${modal === 'assign' ? 'active' : ''}`}>
        <div className="modal-box">
          <div className="modal-handle"></div>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 20, marginBottom: 4 }}>Assign Technician</div>
          {activeRequest && <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 20 }}>{activeRequest.serviceType}</div>}

          {/* Who the company asked for, said before the list rather than
              after it. They cannot see who is booked out on Tuesday, so it is
              a request and not an instruction — but it is the first thing to
              know while choosing, not a detail to discover afterwards. */}
          {activeRequest?.requestedVendorName && (
            <div className="alert alert-amber" style={{ marginBottom: 16 }}>
              They asked for <strong>{activeRequest.requestedVendorName}</strong>.
            </div>
          )}

          <div className="form-group">
            <label>Select Technician</label>
            <select value={assignVendorId} onChange={e => setAssignVendorId(e.target.value)}>
              <option value="">Choose a technician…</option>
              {vendors.map(v => (
                <option key={v._id} value={v._id}>{v.businessName}{v.specialty ? ` — ${v.specialty}` : ''}</option>
              ))}
            </select>
          </div>

          {vendors.length === 0 && <div className="alert alert-amber">No technicians registered yet. They need to create an account first.</div>}

          <button className="btn btn-green btn-lg" style={{ marginBottom: 10 }} onClick={handleAssignVendor} disabled={!assignVendorId}>Assign Technician</button>
          <button className="btn btn-ghost btn-lg" onClick={() => setModal(null)}>Cancel</button>
        </div>
      </div>
      {modal === 'assign' && <div className="overlay active" onClick={() => setModal(null)}></div>}

      {/* Completion Photos Modal */}
      <div className={`modal ${modal === 'completion' ? 'active' : ''}`}>
        <div className="modal-box">
          <div className="modal-handle"></div>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 20, marginBottom: 4 }}>{t('m.submitCompletion')}</div>
          {activeRequest && <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 20 }}>{optLabel(activeRequest.serviceType)}</div>}

          <div className="form-group">
            <label>{t('m.completionPhotos')} <span className="req">*</span></label>
            <div className="upload-zone" onClick={() => document.getElementById('completion-input').click()} style={{ cursor: 'pointer' }}>
              <input id="completion-input" type="file" accept="image/*,video/*" multiple style={{ display: 'none' }} onChange={e => setCompletionFiles(Array.from(e.target.files))} />
              {completionFiles.length > 0 ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'center' }}>
                  {completionFiles.map((f, i) => <span key={i} className="file-chip">{f.name}</span>)}
                </div>
              ) : (
                <p style={{ fontSize: 13, color: 'var(--muted)' }}><strong style={{ color: 'var(--blue)' }}>{t('req.tapToUpload')}</strong> {t('m.beforeAfter')}</p>
              )}
            </div>
          </div>
          <div className="form-group"><label>{t('m.completionNotes')}</label><textarea value={completionNote} onChange={e => setCompletionNote(e.target.value)} placeholder={t('m.describeDone')} /></div>

          <button className="btn btn-green btn-lg" style={{ marginBottom: 10 }} onClick={handleSubmitCompletion}>{t('m.submitCompletion')}</button>
          <button className="btn btn-ghost btn-lg" onClick={() => setModal(null)}>{t('req.cancel')}</button>
        </div>
      </div>
      {modal === 'completion' && <div className="overlay active" onClick={() => setModal(null)}></div>}

      {/* Invoice Modal */}
      <div className={`modal ${modal === 'invoice' ? 'active' : ''}`}>
        <div className="modal-box">
          <div className="modal-handle"></div>
          {/* The tech is submitting his work for billing; Julian is billing a
              management company. Same record, and it reads wrong to both if it
              only ever says one of them. */}
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 20, marginBottom: 4 }}>
            {currentUser?.role === 'admin' ? 'Invoice' : t('vd.submitInvoice')}
          </div>
          {activeRequest && (
            <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 20 }}>
              {[optLabel(activeRequest.serviceType), activeRequest.unitLabel, activeRequest.serviceAddress]
                .filter(Boolean).join(' · ')}
            </div>
          )}

          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--blue)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 10 }}>{t('m.lineItems')}</div>
          {invoiceForm.lineItems.map((row, i) => (
            <div key={i} className="inv-line-row">
              <input className="li-input" placeholder={t('m.description')} value={row.description} onChange={e => {
                const li = [...invoiceForm.lineItems];
                li[i] = { ...li[i], description: e.target.value };
                setInvoiceForm(p => ({ ...p, lineItems: li }));
              }} />
              <input className="li-input" placeholder={t('m.qty')} type="number" value={row.qty} onChange={e => {
                const li = [...invoiceForm.lineItems];
                const qty = parseFloat(e.target.value) || 0;
                const uc = parseFloat(li[i].unitCost) || 0;
                li[i] = { ...li[i], qty: e.target.value, total: (qty * uc).toFixed(2) };
                setInvoiceForm(p => ({ ...p, lineItems: li }));
              }} />
              <input className="li-input" placeholder={t('m.unit')} type="number" value={row.unitCost} onChange={e => {
                const li = [...invoiceForm.lineItems];
                const uc = parseFloat(e.target.value) || 0;
                const qty = parseFloat(li[i].qty) || 0;
                li[i] = { ...li[i], unitCost: e.target.value, total: (qty * uc).toFixed(2) };
                setInvoiceForm(p => ({ ...p, lineItems: li }));
              }} />
              <input className="li-input" placeholder={t('m.total')} value={row.total} readOnly style={{ background: 'var(--bg)' }} />
              <button className="btn btn-ghost btn-sm btn-icon" onClick={() => {
                const li = invoiceForm.lineItems.filter((_, idx) => idx !== i);
                setInvoiceForm(p => ({ ...p, lineItems: li.length ? li : [{ description: '', qty: 1, unitCost: '', total: '' }] }));
              }}>×</button>
            </div>
          ))}
          <button className="btn btn-ghost btn-sm" style={{ marginBottom: 16 }} onClick={() => setInvoiceForm(p => ({ ...p, lineItems: [...p.lineItems, { description: '', qty: 1, unitCost: '', total: '' }] }))}>{t('m.addLine')}</button>

          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderTop: '2px solid var(--border)', marginBottom: 16, fontSize: 16, fontWeight: 800 }}>
            <span>{t('m.invoiceTotal')}</span>
            <span style={{ color: 'var(--green)' }}>{fmtMoney(invoiceTotal)}</span>
          </div>

          <div className="form-group"><label>{t('m.notes')}</label><textarea value={invoiceForm.notes} onChange={e => setInvoiceForm(p => ({ ...p, notes: e.target.value }))} placeholder={t('m.anyNotes')} /></div>

          <button className="btn btn-green btn-lg" style={{ marginBottom: 10 }} onClick={handleSubmitInvoice}>
            {currentUser?.role === 'admin' ? 'Send Invoice' : t('vd.submitInvoice')}
          </button>
          <button className="btn btn-ghost btn-lg" onClick={() => setModal(null)}>{t('req.cancel')}</button>
        </div>
      </div>
      {modal === 'invoice' && <div className="overlay active" onClick={() => setModal(null)}></div>}

      {/* Add Lead Modal (admin logs a lead from Facebook, referral, phone, etc.) */}
      <div className={`modal ${modal === 'addLead' ? 'active' : ''}`}>
        <div className="modal-box">
          <div className="modal-handle"></div>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 20, marginBottom: 4 }}>Add a Lead</div>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 20 }}>Log a lead from Facebook, Nextdoor, a referral, or a phone call.</div>

          <div className="form-row">
            <div className="form-group"><label>Full Name <span className="req">*</span></label><input type="text" value={addLeadForm.fullName} onChange={e => setLead({ fullName: e.target.value })} placeholder="Jane Doe" /></div>
            <div className="form-group"><label>Phone <span className="req">*</span></label><input type="tel" value={addLeadForm.phone} onChange={e => setLead({ phone: e.target.value })} placeholder="(801) 555-0100" /></div>
          </div>
          <div className="form-group"><label>Email</label><input type="email" value={addLeadForm.email} onChange={e => setLead({ email: e.target.value })} placeholder="Optional" /></div>

          <div className="form-group"><label>City</label>
            <select value={addLeadForm.city} onChange={e => setLead({ city: e.target.value })}>
              <option value="">Select city…</option>
              {BRAND.serviceArea.primaryCities.map(c => <option key={c} value={c}>{c}</option>)}
              <option value="Other">Other / outside area</option>
            </select>
          </div>
          {addLeadForm.city === 'Other' && (
            <div className="form-group"><label>Which city?</label><input type="text" value={addLeadForm.cityOther} onChange={e => setLead({ cityOther: e.target.value })} placeholder="Enter city" /></div>
          )}
          <div className="form-group"><label>Service Address</label><input type="text" value={addLeadForm.serviceAddress} onChange={e => setLead({ serviceAddress: e.target.value })} placeholder="Street address (optional)" /></div>

          <div className="form-row">
            <div className="form-group"><label>Lead Source</label>
              <select value={addLeadForm.leadSource} onChange={e => setLead({ leadSource: e.target.value })}>
                {LEAD_SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="form-group"><label>Urgency</label>
              <select value={addLeadForm.urgency} onChange={e => setLead({ urgency: e.target.value })}>
                <option>Standard – 1 week</option><option>Soon – 2-3 days</option><option>Urgent – 24 hrs</option>
              </select>
            </div>
          </div>

          <div className="form-group"><label>Service Type</label>
            <select value={addLeadForm.serviceType} onChange={e => setLead({ serviceType: e.target.value })}>
              <option value="">Select…</option>
              <option>Handyman – General Repair</option>
              <option>Roofing</option>
              <option>Siding</option>
              <option>Storm Damage</option>
              <option>Remodel – Kitchen</option>
              <option>Remodel – Bathroom</option>
              <option>Basement Finish</option>
              <option>Other</option>
            </select>
          </div>
          {/* What they need, a line per job — the same list the website form
              collects and the same one the text conversation builds, on the
              same editor screen. Julian on the phone hears "the faucet, and
              the gate, and the drywall" as often as anyone, and a lead that
              holds them separately is a lead he can price line by line
              without re-reading a paragraph. */}
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--blue)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>What do they need?</div>
          <div style={{ fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.5, marginBottom: 10 }}>
            A line per job. Tap one to write the note and add photos.
          </div>
          {(addLeadForm.items || []).map((it, i) => {
            const shots = (it.photos || []).length;
            return (
              <button type="button" key={i} className="item-row" onClick={() => setEditingLeadItem(i)}>
                <span style={{ minWidth: 0 }}>
                  <span className="item-row-t" style={{ display: 'block' }}>{it.title || it.description || 'New job'}</span>
                  <span className="item-row-s" style={{ display: 'block' }}>
                    {it.serviceType || addLeadForm.serviceType || 'Tap to fill this in'}
                    {shots > 0 && ` · ${shots} photo${shots === 1 ? '' : 's'}`}
                  </span>
                </span>
                <span className="item-row-amt" style={{ color: 'var(--blue)' }}>›</span>
              </button>
            );
          })}
          <button type="button" className="btn btn-ghost btn-sm" style={{ marginBottom: 16 }} onClick={() => {
            const items = [...(addLeadForm.items || []), { title: '', serviceType: '', description: '', photos: [] }];
            setLead({ items });
            setEditingLeadItem(items.length - 1);
          }}>+ Add another job</button>

          <div className="form-group"><label>Appointment (in-person estimate)</label><input type="datetime-local" value={addLeadForm.appointmentAt} onChange={e => setLead({ appointmentAt: e.target.value })} /></div>

          <button className="btn btn-primary btn-lg" style={{ marginBottom: 10 }} onClick={handleAddLead}>Add Lead</button>
          <button className="btn btn-ghost btn-lg" onClick={() => setModal(null)}>Cancel</button>
        </div>
      </div>
      {modal === 'addLead' && <div className="overlay active" onClick={() => setModal(null)}></div>}

      {/* One job on the lead, on its own screen — the same component the
          customer's request form uses. The difference is `onUploadFiles`:
          Julian is signed in, so his photos go straight to Cloudinary as he
          picks them, the way a technician's estimate lines already work. */}
      {modal === 'addLead' && editingLeadItem !== null && addLeadForm.items?.[editingLeadItem] && (
        <RequestItemEditor
          item={addLeadForm.items[editingLeadItem]}
          index={editingLeadItem}
          total={addLeadForm.items.length}
          serviceTypes={SERVICE_TYPES}
          onUploadFiles={uploadPhotos}
          onChange={(next) => {
            const items = [...addLeadForm.items];
            items[editingLeadItem] = next;
            setLead({ items });
          }}
          onDelete={() => {
            const items = addLeadForm.items.filter((_, i) => i !== editingLeadItem);
            setLead({ items: items.length ? items : [{ title: '', serviceType: '', description: '', photos: [] }] });
            setEditingLeadItem(null);
          }}
          onClose={() => setEditingLeadItem(null)}
        />
      )}

      {/* ── Add a Client ───────────────────────────────────────────────────
          The website only ever creates a client as a by-product of somebody
          filing a request. This is for the customer Julian already has: taken
          down on the phone, with the address the work is at and the address the
          invoice goes to — the second of which had nowhere to live before.

          A full screen rather than a bottom sheet, laid out like Joist's Edit
          Client — see components/ClientEditor.js. */}
      {modal === 'addClient' && addClientForm && (
        <ClientEditor
          form={addClientForm}
          setForm={setAddClientForm}
          onSave={handleAddClient}
          saving={isSavingClient}
          clients={clients}
          onPickClient={openClientForm}
          link={lastClientLink}
          onCopyLink={() => { navigator.clipboard?.writeText(lastClientLink); showToast('Link copied'); }}
          onClose={() => { setModal(null); setAddClientForm(null); setLastClientLink(''); }}
        />
      )}

      {/* ── Invoice a client ───────────────────────────────────────────────
          The same full screen the estimate is written on (components/
          DocEditor.js) — because an invoice is the same act twice: the
          estimate says what the work will cost, the invoice says what it did,
          and both are a client, a set of described lines, and a total.

          Both ways in land here: Create Invoice on a client's page, and the
          Invoice button on one of their estimates, which arrives with the
          agreed lines already on it. */}
      {modal === 'clientInvoice' && clientInvoiceForm && (() => {
        const f = clientInvoiceForm;
        const setInv = (patch) => setClientInvoiceForm(p => ({ ...p, ...patch }));
        // Only this client's projects: an invoice on somebody else's statement
        // is a mistake nobody catches until they read the bill.
        const theirs = (projects || []).filter(p => String(p.homeownerId?._id || p.homeownerId) === String(f.client._id));
        return (
          <DocEditor
            title={f.requestId ? 'Invoice This Estimate' : 'New Invoice'}
            clientName={f.client.name}
            clientSub={[
              f.client.phone,
              [f.client.address, f.client.city].filter(Boolean).join(', '),
              f.requestId ? 'Carried over from the estimate they agreed' : '',
            ].filter(Boolean).join(' · ')}
            header={
              <div className="de-fields">
                <div className="form-group"><label>Which project</label>
                  <select value={f.projectId} onChange={e => setInv({ projectId: e.target.value })}>
                    <option value="">A new project</option>
                    {theirs.map(p => <option key={p._id} value={p._id}>{p.title}</option>)}
                  </select>
                </div>
                {!f.projectId && (
                  <div className="form-group"><label>Project name <span className="req">*</span></label>
                    <input type="text" value={f.title} onChange={e => setInv({ title: e.target.value })}
                      placeholder="Back gate" /></div>
                )}
                <div className="form-row">
                  <div className="form-group"><label>Invoice #</label>
                    <input type="text" value={f.invoiceNumber} onChange={e => setInv({ invoiceNumber: e.target.value })}
                      placeholder="Assigned for you" /></div>
                  <div className="form-group"><label>Date</label>
                    <input type="date" value={f.invoiceDate} onChange={e => setInv({ invoiceDate: e.target.value })} /></div>
                </div>
                {/* Left blank it comes back as INV-<year>-<0000>, counted on
                    the server. Typed over when the invoice already exists in
                    Joist and this is the statement catching up. */}
                <div style={{ fontSize: 12, color: 'var(--muted)', margin: '-4px 0 4px', lineHeight: 1.5 }}>
                  Leave the number blank and one is assigned. Type one to match an invoice you have already raised elsewhere.
                </div>
              </div>
            }
            lines={f.lines}
            onChangeLines={(lines) => setInv({ lines })}
            adjustments={[
              { key: 'discount', label: 'Discount', value: f.discount, sign: -1, onChange: (v) => setInv({ discount: v }) },
              { key: 'tax', label: 'Tax', value: f.tax, sign: 1, onChange: (v) => setInv({ tax: v }) },
            ]}
            doneLabel="Raise Invoice"
            onDone={handleSaveClientInvoice}
            saving={isSavingInvoice}
            backLabel="Invoice"
            onCancel={() => { setModal(null); setClientInvoiceForm(null); }}
          />
        );
      })()}

      {/* Set Appointment Modal (for an existing lead) */}
      <div className={`modal ${modal === 'appointment' ? 'active' : ''}`}>
        <div className="modal-box">
          <div className="modal-handle"></div>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 20, marginBottom: 4 }}>Schedule Appointment</div>
          {activeRequest && <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 20 }}>{activeRequest.fullName} · {activeRequest.serviceType}</div>}

          <div className="form-group"><label>Date & Time of in-person estimate</label><input type="datetime-local" value={appointmentInput} onChange={e => setAppointmentInput(e.target.value)} /></div>

          <button className="btn btn-primary btn-lg" style={{ marginBottom: 10 }} onClick={handleSetAppointment}>Save Appointment</button>
          {appointmentInput && <button className="btn btn-ghost btn-lg" style={{ marginBottom: 10 }} onClick={() => setAppointmentInput('')}>Clear</button>}
          <button className="btn btn-ghost btn-lg" onClick={() => setModal(null)}>Cancel</button>
        </div>
      </div>
      {modal === 'appointment' && <div className="overlay active" onClick={() => setModal(null)}></div>}

      {/* ── Add a commercial account ─────────────────────────────────────
          Self-signup at /commercial is for the company that finds us. This is
          for the one Julian is already working with, who is not going to fill
          in a web form because he asked them to. */}
      <div className={`modal ${modal === 'adminAccount' ? 'active' : ''}`}>
        <div className="modal-box">
          <div className="modal-handle"></div>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 20, marginBottom: 4 }}>Add a Commercial Account</div>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 20 }}>A property manager, HOA, realtor or investor.</div>

          {accountForm && (
            <>
              <div className="form-group"><label>Company name <span className="req">*</span></label>
                <input type="text" value={accountForm.name} onChange={e => setAccountForm(p => ({ ...p, name: e.target.value }))} placeholder="Conrad Cruz Property Management" /></div>
              <div className="form-group"><label>What kind</label>
                <select value={accountForm.type} onChange={e => setAccountForm(p => ({ ...p, type: e.target.value }))}>
                  <option value="property-management">Property management</option>
                  <option value="hoa">HOA</option>
                  <option value="realtor">Realtor</option>
                  <option value="investor">Investor</option>
                  <option value="other">Other</option>
                </select></div>
              <div className="form-row">
                <div className="form-group"><label>Office phone</label>
                  <input type="tel" value={accountForm.phone} onChange={e => setAccountForm(p => ({ ...p, phone: e.target.value }))} /></div>
                <div className="form-group"><label>Office email</label>
                  <input type="email" value={accountForm.email} onChange={e => setAccountForm(p => ({ ...p, email: e.target.value }))} /></div>
              </div>
              <div className="form-group"><label>Billing address</label>
                <input type="text" value={accountForm.billingAddress} onChange={e => setAccountForm(p => ({ ...p, billingAddress: e.target.value }))} /></div>
              <div className="form-group">
                <label>Spending limit (leave blank for none)</label>
                <input type="number" inputMode="decimal" min="0" value={accountForm.defaultNteAmount}
                  onChange={e => setAccountForm(p => ({ ...p, defaultNteAmount: e.target.value }))} placeholder="e.g. 300" />
                {/* Worth saying plainly: blank is not "no rules", it is "ask me
                    every time", which feels broken from their side. */}
                <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 6, lineHeight: 1.5 }}>
                  Pre-fills every work order they raise. Left blank, <strong>every estimate comes back to them
                  to approve</strong> — agree a number with them first.
                </div>
              </div>

              <div style={{ borderTop: '1px solid var(--border)', margin: '18px 0 14px', paddingTop: 16 }}>
                <div className="sec-label">Their first login (optional)</div>
                <div style={{ fontSize: 12.5, color: 'var(--muted)', marginBottom: 12, lineHeight: 1.5 }}>
                  Whoever you name here becomes the <strong>owner</strong> of the account and can add the rest of
                  their team themselves. You can leave it and add them later.
                </div>
                <div className="form-group"><label>Their name</label>
                  <input type="text" value={accountForm.ownerName} onChange={e => setAccountForm(p => ({ ...p, ownerName: e.target.value }))} placeholder="Courtney Diment" /></div>
                <div className="form-row">
                  <div className="form-group"><label>Their email</label>
                    <input type="email" value={accountForm.ownerEmail} onChange={e => setAccountForm(p => ({ ...p, ownerEmail: e.target.value }))} /></div>
                  <div className="form-group"><label>Their phone</label>
                    <input type="tel" value={accountForm.ownerPhone} onChange={e => setAccountForm(p => ({ ...p, ownerPhone: e.target.value }))} /></div>
                </div>
                <label className="same-check" style={{ alignItems: 'flex-start', lineHeight: 1.5 }}>
                  <input type="checkbox" checked={accountForm.sendInvite !== false}
                    onChange={e => setAccountForm(p => ({ ...p, sendInvite: e.target.checked }))} style={{ marginTop: 3 }} />
                  <span style={{ fontSize: 13 }}>Email them a link to set their own password. Untick if you would
                    rather read them the link yourself — either way no password is ever sent by email.</span>
                </label>
              </div>

              <button className="btn btn-primary btn-lg" style={{ marginBottom: 10 }} onClick={handleCreateAccount}>Create Account</button>
            </>
          )}

          {/* Shown after a successful invite. Reading this out is more reliable
              than an email landing in a spam folder nobody checks. */}
          {lastInviteLink && (
            <div className="alert alert-green" style={{ marginBottom: 12, wordBreak: 'break-all', fontSize: 12.5, flexDirection: 'column' }}>
              <strong style={{ display: 'block', marginBottom: 6 }}>Their set-password link — good for 7 days</strong>
              {lastInviteLink}
              <button className="btn btn-outline btn-sm" style={{ marginTop: 10 }}
                onClick={() => { navigator.clipboard?.writeText(lastInviteLink); showToast('Link copied'); }}>Copy link</button>
            </div>
          )}

          <button className="btn btn-ghost btn-lg" onClick={() => { setModal(null); setAccountForm(null); setLastInviteLink(''); }}>
            {lastInviteLink ? 'Done' : 'Cancel'}
          </button>
        </div>
      </div>
      {modal === 'adminAccount' && <div className="overlay active" onClick={() => { setModal(null); setLastInviteLink(''); }}></div>}

      {/* ── Add somebody to a commercial account ────────────────────────── */}
      <div className={`modal ${modal === 'adminAccountMember' ? 'active' : ''}`}>
        <div className="modal-box">
          <div className="modal-handle"></div>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 20, marginBottom: 4 }}>
            {accountMemberForm?.userId ? 'Access Level' : 'Add Someone'}
          </div>
          {accountMemberForm && (
            <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 20 }}>{accountMemberForm.accountName}</div>
          )}

          {accountMemberForm && (
            <>
              {!accountMemberForm.userId ? (
                <>
                  <div className="form-group"><label>Their name <span className="req">*</span></label>
                    <input type="text" value={accountMemberForm.name} onChange={e => setAccountMemberForm(p => ({ ...p, name: e.target.value }))} /></div>
                  <div className="form-group"><label>Their email <span className="req">*</span></label>
                    <input type="email" value={accountMemberForm.email} onChange={e => setAccountMemberForm(p => ({ ...p, email: e.target.value }))} /></div>
                  <div className="form-group"><label>Their phone</label>
                    <input type="tel" value={accountMemberForm.phone} onChange={e => setAccountMemberForm(p => ({ ...p, phone: e.target.value }))} /></div>
                </>
              ) : (
                <div style={{ fontSize: 14, marginBottom: 16 }}>{accountMemberForm.name} · {accountMemberForm.email}</div>
              )}

              {/* Radio rows, not a dropdown: the difference between these three
                  IS the point, and a select hides two thirds of it behind a tap. */}
              <div className="form-group">
                <label>What they can do</label>
                {[
                  ['owner', 'Owner', 'Everything, plus adding people and changing company settings.'],
                  ['manager', 'Manager', 'Raise work orders and approve estimates that go over the limit.'],
                  ['coordinator', 'Coordinator', 'Raise work orders and follow them. Cannot approve spending over the limit.'],
                ].map(([value, label, desc]) => (
                  <label key={value} className="same-check" style={{ alignItems: 'flex-start', lineHeight: 1.5, marginBottom: 10 }}>
                    <input type="radio" name="adminMemberLevel" checked={accountMemberForm.level === value}
                      onChange={() => setAccountMemberForm(p => ({ ...p, level: value }))} style={{ marginTop: 3 }} />
                    <span>
                      <span style={{ display: 'block', fontSize: 14, color: 'var(--navy)' }}>{label}</span>
                      <span style={{ display: 'block', fontSize: 12.5, color: 'var(--muted)', marginTop: 2 }}>{desc}</span>
                    </span>
                  </label>
                ))}
              </div>

              {!accountMemberForm.userId && (
                <label className="same-check" style={{ alignItems: 'flex-start', lineHeight: 1.5, marginBottom: 14 }}>
                  <input type="checkbox" checked={accountMemberForm.sendInvite !== false}
                    onChange={e => setAccountMemberForm(p => ({ ...p, sendInvite: e.target.checked }))} style={{ marginTop: 3 }} />
                  <span style={{ fontSize: 13 }}>Email them a link to set their own password.</span>
                </label>
              )}

              <button className="btn btn-primary btn-lg" style={{ marginBottom: 10 }} onClick={handleSaveAccountMember}>
                {accountMemberForm.userId ? 'Save' : 'Add Them'}
              </button>
            </>
          )}

          {lastInviteLink && (
            <div className="alert alert-green" style={{ marginBottom: 12, wordBreak: 'break-all', fontSize: 12.5, flexDirection: 'column' }}>
              <strong style={{ display: 'block', marginBottom: 6 }}>Their set-password link — good for 7 days</strong>
              {lastInviteLink}
              <button className="btn btn-outline btn-sm" style={{ marginTop: 10 }}
                onClick={() => { navigator.clipboard?.writeText(lastInviteLink); showToast('Link copied'); }}>Copy link</button>
            </div>
          )}

          <button className="btn btn-ghost btn-lg" onClick={() => { setModal(null); setAccountMemberForm(null); setLastInviteLink(''); }}>
            {lastInviteLink ? 'Done' : 'Cancel'}
          </button>
        </div>
      </div>
      {modal === 'adminAccountMember' && <div className="overlay active" onClick={() => { setModal(null); setLastInviteLink(''); }}></div>}

      {/* ── Add a property to a commercial account ───────────────────────
          Their own people can add properties from their portal. This is the
          same act on the day the account is brought on board, when nobody
          there has signed in yet and Julian has the buildings in front of him.

          Only the address is required. The gate and lockbox codes are here
          because they are what a tech is actually stuck without, and asking
          for them while the manager is still on the phone is the only time
          anyone reliably answers. Everything else the property record holds —
          the furnace, the owner, the units — they fill in from their side. */}
      <div className={`modal ${modal === 'adminProperty' ? 'active' : ''}`}>
        <div className="modal-box">
          <div className="modal-handle"></div>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 20, marginBottom: 4 }}>Add a Property</div>
          {adminPropertyForm && (
            <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 20 }}>{adminPropertyForm.accountName}</div>
          )}

          {adminPropertyForm && (
            <>
              <div className="form-group"><label>Street address <span className="req">*</span></label>
                <input type="text" value={adminPropertyForm.addressLine1}
                  onChange={e => setAdminPropertyForm(p => ({ ...p, addressLine1: e.target.value }))} placeholder="400 W Main St" /></div>
              <div className="form-group"><label>Unit / suite</label>
                <input type="text" value={adminPropertyForm.addressLine2}
                  onChange={e => setAdminPropertyForm(p => ({ ...p, addressLine2: e.target.value }))} placeholder="Apt 3" /></div>
              {/* Three across rather than the usual two: the state is two
                  characters and does not deserve a line of its own, and Julian
                  takes properties in Idaho and Wyoming as well as Utah. */}
              <div className="form-row" style={{ gridTemplateColumns: '2fr 1fr 1.2fr' }}>
                <div className="form-group"><label>City</label>
                  <input type="text" value={adminPropertyForm.city}
                    onChange={e => setAdminPropertyForm(p => ({ ...p, city: e.target.value }))} /></div>
                <div className="form-group"><label>State</label>
                  <input type="text" maxLength={2} autoCapitalize="characters" value={adminPropertyForm.state}
                    onChange={e => setAdminPropertyForm(p => ({ ...p, state: e.target.value.toUpperCase() }))} /></div>
                <div className="form-group"><label>ZIP</label>
                  <input type="text" inputMode="numeric" value={adminPropertyForm.zip}
                    onChange={e => setAdminPropertyForm(p => ({ ...p, zip: e.target.value }))} /></div>
              </div>
              <div className="form-group"><label>What kind</label>
                <select value={adminPropertyForm.propertyType}
                  onChange={e => setAdminPropertyForm(p => ({ ...p, propertyType: e.target.value }))}>
                  <option value="single-family">Single family</option>
                  <option value="condo">Condo</option>
                  <option value="townhome">Townhome</option>
                  <option value="duplex">Duplex</option>
                  <option value="multiplex">Multiplex</option>
                  <option value="apartment">Apartment</option>
                  <option value="commercial">Commercial</option>
                  <option value="other">Other</option>
                </select></div>
              <div className="form-group"><label>What they call it</label>
                <input type="text" value={adminPropertyForm.label}
                  onChange={e => setAdminPropertyForm(p => ({ ...p, label: e.target.value }))} placeholder="Cedar Park 4-plex" /></div>
              <div className="form-group">
                <label>Their code for it</label>
                <input type="text" value={adminPropertyForm.externalId}
                  onChange={e => setAdminPropertyForm(p => ({ ...p, externalId: e.target.value }))} placeholder="P-100" />
                {/* Their own id out of AppFolio or Buildium. It is what makes our
                    records match their books, and what a later spreadsheet
                    import matches on instead of creating a second copy. */}
                <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 6, lineHeight: 1.5 }}>
                  Their number for this property in AppFolio, Buildium or Yardi — it keeps our records lined up with theirs.
                </div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Gate code</label>
                  <input type="text" value={adminPropertyForm.access?.gateCode || ''}
                    onChange={e => setAdminPropertyForm(p => ({ ...p, access: { ...(p.access || {}), gateCode: e.target.value } }))} /></div>
                <div className="form-group"><label>Lockbox code</label>
                  <input type="text" value={adminPropertyForm.access?.lockboxCode || ''}
                    onChange={e => setAdminPropertyForm(p => ({ ...p, access: { ...(p.access || {}), lockboxCode: e.target.value } }))} /></div>
              </div>
              <div className="form-group">
                <label>Spending limit for this property</label>
                <input type="number" inputMode="decimal" min="0" value={adminPropertyForm.nteAmount}
                  onChange={e => setAdminPropertyForm(p => ({ ...p, nteAmount: e.target.value }))} placeholder="Uses the company limit" />
                <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 6, lineHeight: 1.5 }}>
                  Leave blank and this property uses the company-wide limit. Set one only where this building needs to be different.
                </div>
              </div>

              <button className="btn btn-primary btn-lg" style={{ marginBottom: 10 }} onClick={handleCreateAdminProperty}>Add Property</button>
            </>
          )}

          <button className="btn btn-ghost btn-lg" onClick={() => { setModal(null); setAdminPropertyForm(null); }}>Cancel</button>
        </div>
      </div>
      {modal === 'adminProperty' && <div className="overlay active" onClick={() => setModal(null)}></div>}

      {/* ── Start a job at a property, to price it or to bill it ─────────
          Short on purpose. The address, the unit's tenant, the access codes and
          the spending limit are all on the property already — the only thing
          nobody can know is what the work is, and Julian is usually standing in
          front of it. Saving opens the estimate or the invoice sheet next. */}
      <div className={`modal ${modal === 'adminPropertyJob' ? 'active' : ''}`}>
        <div className="modal-box">
          <div className="modal-handle"></div>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 20, marginBottom: 4 }}>
            {propertyJobForm?.next === 'invoice' ? 'New Invoice' : 'New Estimate'}
          </div>
          {propertyJobForm && (
            <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 20 }}>{propertyJobForm.propertyLabel}</div>
          )}

          {propertyJobForm && (
            <>
              {/* A property with units needs one named. Billing "400 W Main"
                  when the work was in #103 is how the wrong owner gets charged. */}
              {propertyJobForm.units?.length > 0 && (
                <div className="form-group">
                  <label>Which unit</label>
                  <select value={propertyJobForm.unitId} onChange={e => setPropertyJobForm(p => ({ ...p, unitId: e.target.value }))}>
                    <option value="">The whole building</option>
                    {propertyJobForm.units.map(u => (
                      <option key={u._id} value={u._id}>#{u.unitNumber}{u.tenantName ? ` · ${u.tenantName}` : ''}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="form-group">
                <label>Kind of work</label>
                <select value={propertyJobForm.serviceType} onChange={e => setPropertyJobForm(p => ({ ...p, serviceType: e.target.value }))}>
                  {SERVICE_TYPES.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>

              <div className="form-group">
                <label>What is the work <span className="req">*</span></label>
                <textarea rows="3" value={propertyJobForm.description}
                  onChange={e => setPropertyJobForm(p => ({ ...p, description: e.target.value }))}
                  placeholder="Full turn — paint, carpet clean, replace the disposal" />
              </div>

              <div className="form-group">
                <label>How urgent</label>
                <select value={propertyJobForm.priority} onChange={e => setPropertyJobForm(p => ({ ...p, priority: e.target.value }))}>
                  <option value="routine">Routine</option>
                  <option value="urgent">Urgent — 48 hours</option>
                  <option value="emergency">Emergency — same day</option>
                </select>
              </div>

              <button className="btn btn-primary btn-lg" style={{ marginBottom: 10 }} onClick={handleCreatePropertyJob}>
                {propertyJobForm.next === 'invoice' ? 'Next — write the invoice' : 'Next — write the estimate'}
              </button>
            </>
          )}

          <button className="btn btn-ghost btn-lg" onClick={() => { setModal(null); setPropertyJobForm(null); }}>Cancel</button>
        </div>
      </div>
      {modal === 'adminPropertyJob' && <div className="overlay active" onClick={() => setModal(null)}></div>}

      {/* Change Password Modal */}
      <div className={`modal ${modal === 'password' ? 'active' : ''}`}>
        <div className="modal-box">
          <div className="modal-handle"></div>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 20, marginBottom: 20 }}>Change Password</div>

          <div className="form-group"><label>Current Password</label><input type="password" autoComplete="current-password" value={passwordForm.current} onChange={e => setPasswordForm(p => ({ ...p, current: e.target.value }))} /></div>
          <div className="form-group"><label>New Password (min 8 characters)</label><input type="password" autoComplete="new-password" value={passwordForm.next} onChange={e => setPasswordForm(p => ({ ...p, next: e.target.value }))} /></div>
          <div className="form-group"><label>Confirm New Password</label><input type="password" autoComplete="new-password" value={passwordForm.confirm} onChange={e => setPasswordForm(p => ({ ...p, confirm: e.target.value }))} /></div>

          <button className="btn btn-primary btn-lg" style={{ marginBottom: 10 }} onClick={handleChangePassword}>Update Password</button>
          <button className="btn btn-ghost btn-lg" onClick={() => setModal(null)}>Cancel</button>
        </div>
      </div>
      {modal === 'password' && <div className="overlay active" onClick={() => setModal(null)}></div>}

      {/* Change the address you sign in with (admin nav → Email) */}
      <div className={`modal ${modal === 'email' ? 'active' : ''}`}>
        <div className="modal-box">
          <div className="modal-handle"></div>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 20, marginBottom: 6 }}>Change Sign-In Email</div>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 18, lineHeight: 1.5 }}>
            This is the address you sign in with. You will use the new one next time.
          </div>

          <div className="form-group"><label>Current Password</label><input type="password" autoComplete="current-password" value={emailForm.current} onChange={e => setEmailForm(p => ({ ...p, current: e.target.value }))} /></div>
          <div className="form-group"><label>New Email</label><input type="email" autoComplete="email" placeholder="you@quickpropertyservices.com" value={emailForm.next} onChange={e => setEmailForm(p => ({ ...p, next: e.target.value }))} /></div>

          <button className="btn btn-primary btn-lg" style={{ marginBottom: 10 }} onClick={handleChangeEmail}>Update Email</button>
          <button className="btn btn-ghost btn-lg" onClick={() => setModal(null)}>Cancel</button>
        </div>
      </div>
      {modal === 'email' && <div className="overlay active" onClick={() => setModal(null)}></div>}

      {/* Confirm Deletion dialog (admin) — centered, Joist-style */}
      {confirmDelete && (
        <>
          <div className="confirm-overlay" onClick={() => setConfirmDelete(null)}></div>
          <div className="confirm-box">
            <div className="confirm-title">Confirm Deletion</div>
            <div className="confirm-text">
              Are you sure you want to delete this {confirmDelete.type}?
              {confirmDelete.label && <><br /><strong>{confirmDelete.label}</strong></>}
              <br /><span style={{ fontSize: 12, color: 'var(--muted)' }}>This can't be undone.</span>
            </div>
            <div className="confirm-actions">
              <button className="btn btn-ghost" onClick={() => setConfirmDelete(null)}>Cancel</button>
              <button className="btn btn-red" onClick={handleConfirmDelete}>Delete</button>
            </div>
          </div>
        </>
      )}

      {/* ── Ledger: new / edit invoice ─────────────────────────── */}
      <div className={`modal ${modal === 'ledgerEntry' ? 'active' : ''}`}>
        <div className="modal-box">
          <div className="modal-handle"></div>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, marginBottom: 4 }}>
            {ledgerForm._id ? 'Edit Ledger Entry' : isAdmin ? 'New Ledger Entry' : t('lg.submitInvoice')}
          </div>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 20, lineHeight: 1.5 }}>
            {isAdmin ? 'Log a vendor invoice so you can track payments against it.' : t('lg.noInvoices')}
          </div>

          {isAdmin && !ledgerForm._id && (
            <div className="form-group">
              <label>Vendor <span className="req">*</span></label>
              <select value={ledgerForm.vendorId} onChange={e => setLedger({ vendorId: e.target.value })}>
                <option value="">Choose a vendor…</option>
                {vendors.map(v => (
                  <option key={v._id} value={v._id}>
                    {v.businessName}{v.compliance && !v.compliance.canBePaid ? ' — paperwork expired' : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          {!ledgerForm._id && (
            <div className="form-group">
              <label>{isAdmin ? 'Link to a job (optional)' : t('lg.tabJobs')}</label>
              <select value={ledgerForm.requestId} onChange={e => {
                const job = requests.find(r => r._id === e.target.value);
                setLedger({
                  requestId: e.target.value,
                  serviceAddress: job?.serviceAddress || ledgerForm.serviceAddress,
                  jobDetails: job ? `${job.serviceType} — ${job.description || ''}`.trim() : ledgerForm.jobDetails
                });
              }}>
                <option value="">—</option>
                {requests.map(r => (
                  <option key={r._id} value={r._id}>{optLabel(r.serviceType)} · {r.serviceAddress}</option>
                ))}
              </select>
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label>Invoice #</label>
              <input type="text" value={ledgerForm.invoiceNumber} onChange={e => setLedger({ invoiceNumber: e.target.value })} placeholder="Auto" />
            </div>
            <div className="form-group">
              <label>{t('lg.invoice')} <span className="req">*</span></label>
              <input type="number" step="0.01" min="0" value={ledgerForm.invoiceAmount} onChange={e => setLedger({ invoiceAmount: e.target.value })} placeholder="$0.00" />
            </div>
          </div>

          <div className="form-group"><label>{t('req.serviceAddress')}</label><input type="text" value={ledgerForm.serviceAddress} onChange={e => setLedger({ serviceAddress: e.target.value })} /></div>
          <div className="form-group"><label>{t('req.jobDetails')}</label><textarea value={ledgerForm.jobDetails} onChange={e => setLedger({ jobDetails: e.target.value })} /></div>

          {!ledgerForm._id && (
            <div className="form-group">
              <label>{t('lg.invoiceFile')}</label>
              <input type="file" accept="image/*,.pdf" multiple onChange={e => {
                setLedgerDocFiles(prev => [...prev, ...Array.from(e.target.files)].slice(0, 5));
                e.target.value = '';
              }} />
              {ledgerDocFiles.length > 0 && (
                <div className="file-chips">
                  {ledgerDocFiles.map((file, i) => (
                    <span key={i} className="file-chip">{file.name}
                      <span style={{ marginLeft: 6, cursor: 'pointer' }} onClick={() => setLedgerDocFiles(prev => prev.filter((_, idx) => idx !== i))}>×</span>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {isAdmin && ledgerForm._id && (
            <div className="form-group">
              <label>Status</label>
              <select value={ledgerForm.status} onChange={e => setLedger({ status: e.target.value })}>
                {LEDGER_STATUSES.map(st => <option key={st} value={st}>{st}</option>)}
              </select>
            </div>
          )}

          <div className="form-group"><label>Notes</label><textarea value={ledgerForm.notes} onChange={e => setLedger({ notes: e.target.value })} /></div>

          <button className="btn btn-primary btn-lg" style={{ marginBottom: 10 }} disabled={isSavingLedger} onClick={handleSaveLedgerEntry}>
            {isSavingLedger ? t('req.submitting') : ledgerForm._id ? 'Save Changes' : isAdmin ? 'Add to Ledger' : t('lg.submitInvoice')}
          </button>
          <button className="btn btn-ghost btn-lg" onClick={() => setModal(null)}>{t('req.cancel')}</button>
        </div>
      </div>
      {modal === 'ledgerEntry' && <div className="overlay active" onClick={() => setModal(null)}></div>}

      {/* ── Ledger: record a payment (admin only, English) ─────── */}
      <div className={`modal ${modal === 'payment' ? 'active' : ''}`}>
        <div className="modal-box">
          <div className="modal-handle"></div>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, marginBottom: 4 }}>Record Payment</div>
          {activeEntry && (
            <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 16 }}>
              {activeEntry.vendorId?.businessName} · {activeEntry.invoiceNumber} · balance {fmtMoney(balanceOf(activeEntry))}
            </div>
          )}

          {/* Server refused: this vendor's insurance or licenses have lapsed. */}
          {paymentBlock && (
            <div className="alert alert-red">
              <div>
                <strong>{paymentBlock.message}</strong>
                <div style={{ marginTop: 4 }}>{(paymentBlock.blockers || []).join(' · ')}</div>
                <div style={{ marginTop: 6 }}>
                  Paying a vendor without current insurance or a valid license puts QPS on the hook legally. Get the
                  renewed document uploaded first — or, if you're paying anyway, write down why below.
                </div>
              </div>
            </div>
          )}

          <div className="form-row">
            <div className="form-group"><label>Amount <span className="req">*</span></label><input type="number" step="0.01" min="0" value={paymentForm.amount} onChange={e => setPay({ amount: e.target.value })} placeholder="$0.00" /></div>
            <div className="form-group"><label>Date</label><input type="date" value={paymentForm.date} onChange={e => setPay({ date: e.target.value })} /></div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Mode of Payment</label>
              <select value={paymentForm.mode} onChange={e => setPay({ mode: e.target.value })}>
                {PAYMENT_MODES.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div className="form-group"><label>Reference</label><input type="text" value={paymentForm.reference} onChange={e => setPay({ reference: e.target.value })} placeholder="Check # / confirmation #" /></div>
          </div>

          <div className="form-group"><label>Note</label><input type="text" value={paymentForm.note} onChange={e => setPay({ note: e.target.value })} placeholder="Optional" /></div>

          {paymentBlock && (
            <div className="form-group">
              <label style={{ color: 'var(--red)' }}>Override reason <span className="req">*</span></label>
              <textarea value={paymentForm.overrideReason} onChange={e => setPay({ overrideReason: e.target.value })} placeholder="e.g. Work predates the lapse — renewal certificate received by text, uploading tomorrow" />
            </div>
          )}

          <button
            className={`btn btn-lg ${paymentBlock ? 'btn-red' : 'btn-green'}`}
            style={{ marginBottom: 10 }}
            disabled={paymentBlock && !paymentForm.overrideReason}
            onClick={handleRecordPayment}
          >
            {paymentBlock ? 'Pay Anyway (Override)' : 'Record Payment'}
          </button>
          <button className="btn btn-ghost btn-lg" onClick={() => setModal(null)}>{t('req.cancel')}</button>
        </div>
      </div>
      {modal === 'payment' && <div className="overlay active" onClick={() => setModal(null)}></div>}

      {/* ── Ledger: live progress report ───────────────────────── */}
      <div className={`modal ${modal === 'progress' ? 'active' : ''}`}>
        <div className="modal-box">
          <div className="modal-handle"></div>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, marginBottom: 4 }}>{t('lg.addProgress')}</div>
          {activeEntry && <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 20 }}>{activeEntry.invoiceNumber}{activeEntry.serviceAddress ? ` · ${activeEntry.serviceAddress}` : ''}</div>}

          <div className="form-group">
            <label>{t('req.photosVideos')}</label>
            <div className="upload-zone" onClick={() => document.getElementById('progress-input').click()} style={{ cursor: 'pointer' }}>
              <input id="progress-input" type="file" accept="image/*,video/*" multiple style={{ display: 'none' }} onChange={e => {
                setProgressFiles(prev => [...prev, ...Array.from(e.target.files)].slice(0, 10));
                e.target.value = '';
              }} />
              {progressFiles.length > 0 ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'center' }}>
                  {progressFiles.map((f, i) => (
                    <span key={i} className="file-chip">{f.name}
                      <span style={{ marginLeft: 6, cursor: 'pointer' }} onClick={(ev) => { ev.stopPropagation(); setProgressFiles(prev => prev.filter((_, idx) => idx !== i)); }}>×</span>
                    </span>
                  ))}
                </div>
              ) : (
                <p style={{ fontSize: 13, color: 'var(--muted)' }}>{t('req.tapToUpload')}</p>
              )}
            </div>
          </div>

          <div className="form-group">
            <label>{t('lg.jobProgress')} %</label>
            <input type="number" min="0" max="100" value={progressForm.percentComplete} onChange={e => setProgressForm(p => ({ ...p, percentComplete: e.target.value }))} placeholder="60" />
          </div>

          <div className="form-group"><label>{t('req.jobDetails')}</label><textarea value={progressForm.note} onChange={e => setProgressForm(p => ({ ...p, note: e.target.value }))} /></div>

          <button className="btn btn-green btn-lg" style={{ marginBottom: 10 }} disabled={isSavingLedger} onClick={handleAddProgress}>
            {isSavingLedger ? t('req.submitting') : t('lg.addProgress')}
          </button>
          <button className="btn btn-ghost btn-lg" onClick={() => setModal(null)}>{t('req.cancel')}</button>
        </div>
      </div>
      {modal === 'progress' && <div className="overlay active" onClick={() => setModal(null)}></div>}

      {/* ── Compliance document (insurance / license) ──────────── */}
      <div className={`modal ${modal === 'credential' ? 'active' : ''}`}>
        <div className="modal-box">
          <div className="modal-handle"></div>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, marginBottom: 4 }}>{t('lg.uploadRenewal')}</div>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 20, lineHeight: 1.5 }}>{t('lg.docsIntro')}</div>

          <div className="form-group">
            <label>{t('lg.tabDocs')} <span className="req">*</span></label>
            <select value={credentialForm.type} onChange={e => setCred({ type: e.target.value })}>
              {CREDENTIAL_TYPES.map(ct => <option key={ct} value={ct}>{credentialLabel(ct)}</option>)}
            </select>
          </div>

          {credentialForm.type === 'other' && (
            <div className="form-group"><label>{t('lg.type.other')} <span className="req">*</span></label><input type="text" value={credentialForm.label} onChange={e => setCred({ label: e.target.value })} /></div>
          )}

          <div className="form-row">
            <div className="form-group"><label>Issuer</label><input type="text" value={credentialForm.issuer} onChange={e => setCred({ issuer: e.target.value })} /></div>
            <div className="form-group"><label>#</label><input type="text" value={credentialForm.number} onChange={e => setCred({ number: e.target.value })} /></div>
          </div>

          <div className="form-group"><label>{t('lg.expiresOn')} <span className="req">*</span></label><input type="date" value={credentialForm.expiresAt} onChange={e => setCred({ expiresAt: e.target.value })} /></div>

          <div className="form-group">
            <label>{t('lg.upload')}</label>
            <input type="file" accept="image/*,.pdf" onChange={e => setCredentialFile(e.target.files[0] || null)} />
            {credentialFile && <div className="file-chips"><span className="file-chip">{credentialFile.name}</span></div>}
          </div>

          <button className="btn btn-primary btn-lg" style={{ marginBottom: 10 }} disabled={isSavingLedger} onClick={handleSaveCredential}>
            {isSavingLedger ? t('req.submitting') : t('lg.upload')}
          </button>
          <button className="btn btn-ghost btn-lg" onClick={() => setModal(null)}>{t('req.cancel')}</button>
        </div>
      </div>
      {modal === 'credential' && <div className="overlay active" onClick={() => setModal(null)}></div>}

      {/* ── Job conversation (homeowner / technician / QPS) ────── */}
      <div className={`modal ${modal === 'messages' ? 'active' : ''}`}>
        <div className="modal-box">
          <div className="modal-handle"></div>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, marginBottom: 4 }}>{t('msg.title')}</div>
          {modal === 'messages' && <MessageThread />}
          <button className="btn btn-ghost btn-lg" style={{ marginTop: 10 }} onClick={() => setModal(null)}>{t('req.cancel')}</button>
        </div>
      </div>
      {modal === 'messages' && <div className="overlay active" onClick={() => setModal(null)}></div>}

      {/* ── Project (admin) ────────────────────────────────────── */}
      <div className={`modal ${modal === 'project' ? 'active' : ''}`}>
        <div className="modal-box">
          <div className="modal-handle"></div>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, marginBottom: 20 }}>
            {projectForm._id ? 'Edit Project' : 'New Project'}
          </div>
          <div className="form-group"><label>Project name <span className="req">*</span></label><input type="text" value={projectForm.title} onChange={e => setProj({ title: e.target.value })} placeholder="Basement Finish" /></div>
          <div className="form-group">
            <label>Client</label>
            <select style={sel} value={projectForm.homeownerId} onChange={e => setProj({ homeownerId: e.target.value })}>
              <option value="">Not linked to an account</option>
              {/* A client added by hand may have a phone and no email, so the
                  second half of the label is whichever one they gave. */}
              {(clients || []).map(c => (
                <option key={c._id} value={c._id}>{[c.name, c.email || c.phone].filter(Boolean).join(' — ')}</option>
              ))}
            </select>
          </div>
          {!projectForm.homeownerId && (
            <div className="form-group"><label>Client name</label><input type="text" value={projectForm.clientName} onChange={e => setProj({ clientName: e.target.value })} /></div>
          )}
          <div className="form-group"><label>Address</label><input type="text" value={projectForm.address} onChange={e => setProj({ address: e.target.value })} /></div>
          {/* The server has always taken a status; there was no way to set one
              from here, so every project stayed Active forever. Only offered on
              a project that exists — a new one starts active by definition. */}
          {projectForm._id && (
            <div className="form-group"><label>Status</label>
              <select style={sel} value={projectForm.status || 'active'} onChange={e => setProj({ status: e.target.value })}>
                <option value="active">Active — work is running</option>
                <option value="completed">Completed — work is done</option>
                <option value="closed">Closed — done and settled up</option>
              </select>
            </div>
          )}
          <div className="form-group"><label>Notes</label><textarea value={projectForm.notes} onChange={e => setProj({ notes: e.target.value })} /></div>
          <button className="btn btn-primary btn-lg" style={{ marginBottom: 10 }} disabled={isSavingProject} onClick={handleSaveProject}>{isSavingProject ? 'Saving…' : 'Save Project'}</button>
          <button className="btn btn-ghost btn-lg" onClick={() => setModal(null)}>Cancel</button>
        </div>
      </div>
      {modal === 'project' && <div className="overlay active" onClick={() => setModal(null)}></div>}

      {/* ── Edit a technician's details (admin) ────────────────────
          Techs fill the application in on a phone and it shows: a misspelled
          name, a digit missing from a number, "Por trabajo" typed into a rate.
          This lets Julian fix it without asking them to redo the form.

          Trades aren't here on purpose — those are the board columns, and Move
          card owns them. Neither are insurance and licence documents, which
          carry expiry dates and gate payments (Add / Renew a Document). */}
      {vendorEditForm && (
        <>
          <div className="modal active">
            <div className="modal-box">
              <div className="modal-handle"></div>
              <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, marginBottom: 4 }}>
                {vendorEditForm._id ? 'Edit Technician' : 'Add a Technician'}
              </div>
              <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 20 }}>
                {vendorEditForm._id
                  ? 'Fix what the application got wrong, without asking them to redo it.'
                  : 'A tech you already work with, who never filled the application in.'}
              </div>

              <div className="form-group"><label>Name</label><input type="text" value={vendorEditForm.contactName} onChange={e => setVendorEdit({ contactName: e.target.value })} placeholder="The person" /></div>
              <div className="form-group"><label>Business Name <span className="req">*</span></label><input type="text" value={vendorEditForm.businessName} onChange={e => setVendorEdit({ businessName: e.target.value })} /></div>
              <div className="form-group"><label>Occupation</label><input type="text" value={vendorEditForm.occupation} onChange={e => setVendorEdit({ occupation: e.target.value })} placeholder="Plumber" /></div>
              <div className="form-group"><label>Phone Number</label><input type="tel" value={vendorEditForm.phone} onChange={e => setVendorEdit({ phone: e.target.value })} /></div>
              <div className="form-group">
                <label>Email Address</label>
                <input type="email" value={vendorEditForm.email} onChange={e => setVendorEdit({ email: e.target.value })} />
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
                  {vendorEditForm._id
                    ? 'This is the contact address on their card. It does not change the email they sign in with.'
                    : 'This becomes the email they sign in with, and where their link to set a password goes. A phone number on its own is fine — they just have no portal until an email is on file.'}
                </div>
              </div>
              <div className="form-group"><label>Business Address</label><input type="text" value={vendorEditForm.address} onChange={e => setVendorEdit({ address: e.target.value })} /></div>
              <div className="form-group"><label>Home Address if any</label><input type="text" value={vendorEditForm.homeAddress} onChange={e => setVendorEdit({ homeAddress: e.target.value })} /></div>
              <div className="form-group"><label>Years of experience</label><input type="text" value={vendorEditForm.yearsExperience} onChange={e => setVendorEdit({ yearsExperience: e.target.value })} placeholder="15" /></div>

              {[
                ['hasTools', 'Has their own tools'],
                ['licensed', 'Licensed'],
                ['insured', 'Insured'],
                ['hasCrew', 'Has a crew'],
              ].map(([field, label]) => (
                <div className="form-group" key={field}>
                  <label>{label}</label>
                  <select style={sel} value={vendorEditForm[field]} onChange={e => setVendorEdit({ [field]: e.target.value })}>
                    <option value="">Not answered</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                </div>
              ))}

              <div className="form-group"><label>Hourly rate</label><input type="text" value={vendorEditForm.hourlyRate} onChange={e => setVendorEdit({ hourlyRate: e.target.value })} placeholder="$45/hr" /></div>
              <div className="form-group"><label>Rate by square foot</label><input type="text" value={vendorEditForm.sqftRate} onChange={e => setVendorEdit({ sqftRate: e.target.value })} placeholder="$3/sq ft" /></div>

              {/* Trades, but only when adding. The board columns ARE the trades,
                  and for a tech already on it Move card owns them — which is why
                  this isn't offered on an edit. A new tech has no card to move
                  yet, so the trades have to be pickable here or they land in
                  "No trade on file" and are never seen again. */}
              {!vendorEditForm._id && (
                <>
                  <div className="sec-label">What do they do?</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                    {TAXONOMY.map(x => {
                      const on = (vendorEditForm.expertise || []).includes(x.cat);
                      return (
                        <button
                          type="button"
                          key={x.cat}
                          className={`btn btn-sm ${on ? 'btn-primary' : 'btn-outline'}`}
                          onClick={() => setVendorEdit({
                            expertise: on
                              ? vendorEditForm.expertise.filter(c => c !== x.cat)
                              : [...(vendorEditForm.expertise || []), x.cat],
                          })}
                        >
                          {x.cat}
                        </button>
                      );
                    })}
                  </div>
                  <div className="form-group"><label>Anything else they do</label>
                    <input type="text" value={vendorEditForm.expertiseOther} onChange={e => setVendorEdit({ expertiseOther: e.target.value })} placeholder="Optional" /></div>
                  {vendorEditForm.email?.trim() && (
                    <label className="same-check" style={{ alignItems: 'flex-start', lineHeight: 1.5, marginBottom: 16 }}>
                      <input type="checkbox" checked={vendorEditForm.sendInvite === true}
                        onChange={e => setVendorEdit({ sendInvite: e.target.checked })} style={{ marginTop: 3 }} />
                      <span style={{ fontSize: 13 }}>Email them a link to set their own password. Either way you get the
                        link on screen to send yourself — no password is ever emailed.</span>
                    </label>
                  )}
                </>
              )}

              <button className="btn btn-primary btn-lg" style={{ marginBottom: 10 }} disabled={isSavingVendor} onClick={handleSaveVendorEdit}>
                {isSavingVendor ? 'Saving…' : vendorEditForm._id ? 'Save Changes' : 'Add Technician'}
              </button>
              <button className="btn btn-ghost btn-lg" onClick={() => setVendorEditForm(null)}>Cancel</button>
            </div>
          </div>
          <div className="overlay active" onClick={() => setVendorEditForm(null)}></div>
        </>
      )}

      {/* Their way into the portal, after adding them. Reading this out is more
          reliable than an email landing in a spam folder nobody checks. */}
      {!vendorEditForm && lastVendorLink && (
        <>
          <div className="modal active">
            <div className="modal-box">
              <div className="modal-handle"></div>
              <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, marginBottom: 16 }}>Technician added</div>
              <div className="alert alert-green" style={{ marginBottom: 12, wordBreak: 'break-all', fontSize: 12.5, flexDirection: 'column' }}>
                <strong style={{ display: 'block', marginBottom: 6 }}>Their set-password link — good for 7 days</strong>
                {lastVendorLink}
                <button className="btn btn-outline btn-sm" style={{ marginTop: 10 }}
                  onClick={() => { navigator.clipboard?.writeText(lastVendorLink); showToast('Link copied'); }}>Copy link</button>
              </div>
              <div style={{ fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.5, marginBottom: 16 }}>
                Their card says <strong>Application pending</strong> until they fill the rest in themselves —
                insurance and licence documents included, which is what decides whether they can be paid.
              </div>
              <button className="btn btn-ghost btn-lg" onClick={() => setLastVendorLink('')}>Done</button>
            </div>
          </div>
          <div className="overlay active" onClick={() => setLastVendorLink('')}></div>
        </>
      )}

      {/* ── Statement line (admin) ─────────────────────────────── */}
      <div className={`modal ${modal === 'projectLine' ? 'active' : ''}`}>
        <div className="modal-box">
          <div className="modal-handle"></div>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, marginBottom: 4 }}>
            {lineForm._id ? 'Edit Invoice Line' : 'Add Invoice Line'}
          </div>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 20 }}>One trade, one Joist invoice. The client sees this row.</div>

          <div className="form-group"><label>Trade / work <span className="req">*</span></label><input type="text" value={lineForm.trade} onChange={e => setLine({ trade: e.target.value })} placeholder="FRAMING" /></div>
          <div className="form-row">
            <div className="form-group"><label>Joist invoice #</label><input type="text" value={lineForm.invoiceNumber} onChange={e => setLine({ invoiceNumber: e.target.value })} placeholder="1691" /></div>
            <div className="form-group"><label>Client price <span className="req">*</span></label><input type="number" step="0.01" value={lineForm.amount} onChange={e => setLine({ amount: e.target.value })} placeholder="$0.00" /></div>
          </div>
          <div className="form-group"><label>Link to the Joist invoice</label><input type="text" value={lineForm.invoiceUrl} onChange={e => setLine({ invoiceUrl: e.target.value })} placeholder="https://…" /></div>

          <div className="form-row">
            <div className="form-group">
              <label>Technician doing it</label>
              <select style={sel} value={lineForm.vendorId} onChange={e => setLine({ vendorId: e.target.value })}>
                <option value="">Not assigned</option>
                {vendors.map(v => <option key={v._id} value={v._id}>{v.businessName}</option>)}
              </select>
            </div>
            <div className="form-group"><label>What it costs you</label><input type="number" step="0.01" value={lineForm.techCost} onChange={e => setLine({ techCost: e.target.value })} placeholder="$0.00" /></div>
          </div>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 14 }}>
            The cost is never shown to the client — it's what makes the profit figure work.
          </div>

          <button className="btn btn-primary btn-lg" style={{ marginBottom: 10 }} disabled={isSavingProject} onClick={handleSaveProjectLine}>{isSavingProject ? 'Saving…' : 'Save Line'}</button>
          <button className="btn btn-ghost btn-lg" onClick={() => setModal(null)}>Cancel</button>
        </div>
      </div>
      {modal === 'projectLine' && <div className="overlay active" onClick={() => setModal(null)}></div>}

      {/* ── Payment from the client (admin) ────────────────────── */}
      <div className={`modal ${modal === 'clientPayment' ? 'active' : ''}`}>
        <div className="modal-box">
          <div className="modal-handle"></div>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, marginBottom: 20 }}>Record Client Payment</div>
          <div className="form-group">
            <label>Toward which invoice</label>
            <select style={sel} value={clientPayForm.lineId} onChange={e => setCPay({ lineId: e.target.value })}>
              <option value="">Spread across the oldest unpaid lines</option>
              {(activeProject?.lines || []).map(l => <option key={l._id} value={l._id}>{l.trade}{l.invoiceNumber ? ` (#${l.invoiceNumber})` : ''}</option>)}
            </select>
          </div>
          <div className="form-row">
            <div className="form-group"><label>Amount <span className="req">*</span></label><input type="number" step="0.01" value={clientPayForm.amount} onChange={e => setCPay({ amount: e.target.value })} placeholder="$0.00" /></div>
            <div className="form-group"><label>Date</label><input type="date" value={clientPayForm.date} onChange={e => setCPay({ date: e.target.value })} /></div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Form of payment</label>
              <select style={sel} value={clientPayForm.method} onChange={e => setCPay({ method: e.target.value })}>
                {PAYMENT_MODES.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div className="form-group"><label>Reference</label><input type="text" value={clientPayForm.reference} onChange={e => setCPay({ reference: e.target.value })} placeholder="Check 104" /></div>
          </div>
          <button className="btn btn-green btn-lg" style={{ marginBottom: 10 }} disabled={isSavingProject} onClick={handleSaveClientPayment}>{isSavingProject ? 'Saving…' : 'Record Payment'}</button>
          <button className="btn btn-ghost btn-lg" onClick={() => setModal(null)}>Cancel</button>
        </div>
      </div>
      {modal === 'clientPayment' && <div className="overlay active" onClick={() => setModal(null)}></div>}

      {/* ── Project expense (admin only) ───────────────────────── */}
      <div className={`modal ${modal === 'projectExpense' ? 'active' : ''}`}>
        <div className="modal-box">
          <div className="modal-handle"></div>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, marginBottom: 4 }}>Log an Expense</div>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 20 }}>Never shown to the client.</div>

          <div className="form-group">
            <label>Which trade</label>
            <select style={sel} value={expenseForm.lineId} onChange={e => setExp({ lineId: e.target.value })}>
              <option value="">Miscellaneous</option>
              {(activeProject?.lines || []).map(l => <option key={l._id} value={l._id}>{l.trade}</option>)}
            </select>
          </div>
          <div className="form-row">
            <div className="form-group"><label>Supplier <span className="req">*</span></label><input type="text" value={expenseForm.supplier} onChange={e => setExp({ supplier: e.target.value })} placeholder="Home Depot" /></div>
            <div className="form-group"><label>Amount <span className="req">*</span></label><input type="number" step="0.01" value={expenseForm.amount} onChange={e => setExp({ amount: e.target.value })} placeholder="$0.00" /></div>
          </div>
          <div className="form-group"><label>What was bought</label><input type="text" value={expenseForm.description} onChange={e => setExp({ description: e.target.value })} /></div>
          <div className="form-group"><label>Payment details</label><input type="text" value={expenseForm.paymentDetails} onChange={e => setExp({ paymentDetails: e.target.value })} placeholder="Card ending 4321 / Check 88" /></div>

          <div className="form-group">
            <label>Charge this to a technician?</label>
            <select style={sel} value={expenseForm.chargeToVendorId} onChange={e => setExp({ chargeToVendorId: e.target.value })}>
              <option value="">No — QPS absorbs it</option>
              {vendors.map(v => <option key={v._id} value={v._id}>Deduct from {v.businessName}</option>)}
            </select>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4, lineHeight: 1.4 }}>
              Pick a technician when you fronted supplies for their work. It comes off what you owe them, and they
              see it on their pay breakdown.
            </div>
          </div>

          <div className="form-group">
            <label>Receipt</label>
            <input type="file" accept="image/*,.pdf" onChange={e => setReceiptFile(e.target.files[0] || null)} />
            {receiptFile && <div className="file-chips"><span className="file-chip">{receiptFile.name}</span></div>}
          </div>

          <button className="btn btn-primary btn-lg" style={{ marginBottom: 10 }} disabled={isSavingProject} onClick={handleSaveExpense}>{isSavingProject ? 'Saving…' : 'Log Expense'}</button>
          <button className="btn btn-ghost btn-lg" onClick={() => setModal(null)}>Cancel</button>
        </div>
      </div>
      {modal === 'projectExpense' && <div className="overlay active" onClick={() => setModal(null)}></div>}

      {/* ── Technician writes an estimate ──────────────────────── */}
      <div className={`modal ${modal === 'techEstimate' ? 'active' : ''}`}>
        <div className="modal-box">
          <div className="modal-handle"></div>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, marginBottom: 4 }}>{t('pj.newEstimate')}</div>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 20, lineHeight: 1.5 }}>
            {t('msg.roleQps')} — {t('pj.materials')} + {t('pj.labor')}
            <br />{t('tp.writeOwnLang')}
          </div>

          <div className="form-group"><label>{t('req.serviceType')}</label><input type="text" value={estimateForm2.trade} onChange={e => setEst({ trade: e.target.value })} placeholder="FRAMING" /></div>
          <div className="form-group"><label>{t('req.serviceAddress')}</label><input type="text" value={estimateForm2.serviceAddress} onChange={e => setEst({ serviceAddress: e.target.value })} /></div>

          {/* Each line opens its own screen — name, the full description,
              photos of the work, quantity and rate. The old five-column grid
              gave the description about 90px on a phone. */}
          {estimateForm2.items.map((row, i) => {
            const amount = (parseFloat(row.qty) || 0) * (parseFloat(row.unitCost) || 0);
            return (
              <button key={i} className="item-row" onClick={() => setEditingItem(i)}>
                <span style={{ minWidth: 0 }}>
                  <span className="item-row-t">
                    {row.name || row.description || t('ie.untitledLine')}
                  </span>
                  <span className="item-row-s">
                    {row.kind === 'material' ? t('pj.materials') : t('pj.labor')} · {row.qty || 1} × {fmtMoney(row.unitCost || 0)}
                    {(row.photos || []).length > 0 && ` · ${row.photos.length} ${t('ie.photos').toLowerCase()}`}
                  </span>
                </span>
                <span className="item-row-amt">{fmtMoney(amount)}</span>
              </button>
            );
          })}
          <button className="btn btn-ghost btn-sm" style={{ marginBottom: 14 }} onClick={() => {
            setEst({ items: [...estimateForm2.items, { name: '', description: '', kind: 'labor', qty: 1, unitCost: '', photos: [] }] });
            setEditingItem(estimateForm2.items.length);
          }}>+ {t('ie.addLine')}</button>

          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderTop: '2px solid var(--border)', marginBottom: 16, fontSize: 16 }}>
            <span>{t('pj.yourEstimateTotal')}</span>
            <span style={{ color: 'var(--green)' }}>{fmtMoney(estimateItemsTotal)}</span>
          </div>

          <div className="form-group"><label>Notes</label><textarea value={estimateForm2.notes} onChange={e => setEst({ notes: e.target.value })} /></div>

          <button className="btn btn-primary btn-lg" style={{ marginBottom: 10 }} disabled={isSavingProject} onClick={handleSubmitTechEstimate}>{isSavingProject ? t('req.submitting') : t('req.submit')}</button>
          <button className="btn btn-ghost btn-lg" onClick={() => setModal(null)}>{t('req.cancel')}</button>
        </div>
      </div>
      {modal === 'techEstimate' && <div className="overlay active" onClick={() => setModal(null)}></div>}

      {/* One line of the estimate, on its own screen (see components/ItemEditor). */}
      {modal === 'techEstimate' && editingItem !== null && estimateForm2.items[editingItem] && (
        <ItemEditor
          item={estimateForm2.items[editingItem]}
          index={editingItem}
          total={estimateForm2.items.length}
          onChange={(next) => {
            const items = [...estimateForm2.items];
            items[editingItem] = next;
            setEst({ items });
          }}
          onDelete={() => {
            const items = estimateForm2.items.filter((_, i) => i !== editingItem);
            setEst({ items: items.length ? items : [{ name: '', description: '', kind: 'labor', qty: 1, unitCost: '', photos: [] }] });
            setEditingItem(null);
          }}
          onClose={() => setEditingItem(null)}
        />
      )}
    </>
  );
}
